import React, { useState, useEffect, useMemo } from "react";
import BaseChart from "./BaseChart";
import { ChartDataService } from "../../services/ChartDataService";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { useChartCache } from "../../hooks/useChartCache";
import { useThemedCharts } from "../../hooks/useThemedCharts";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";

interface LineEfficiencyChartProps {
  lineId?: string; // Line BK — sent as `line_bk` to the backend
  shifts?: string[];
  className?: string;
  /** Which metric to show; controlled by the container's sub-tab. */
  activeTab?: "efficiency" | "utilization";
}

/** One backend row of `PC.line_efficiency`: a machine's value for a shift. */
interface LineEfficiencyRow {
  shift_date: string;
  shift_name: string;
  machine_sn: string;
  machine_name: string;
  efficiency: number;
  utilization: number;
}

/**
 * Line-level efficiency / utilization chart.
 *
 * Unlike the machine chart (which gets per-shift time series from
 * `PC.efficiency` + `PC.utilization`), the line request `PC.line_efficiency`
 * returns one row per MACHINE per shift. So here each machine becomes a series
 * and the categories are the shift-date + shift-name buckets — i.e. grouped
 * columns per machine across shifts.
 */
const LineEfficiencyChart: React.FC<LineEfficiencyChartProps> = ({
  lineId,
  shifts = [],
  activeTab = "efficiency",
}) => {
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const { getSeriesColor } = useThemedCharts();
  const [rows, setRows] = useState<LineEfficiencyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const chartCache = useChartCache<LineEfficiencyRow[]>({
    maxAge: 5 * 60 * 1000,
    maxEntries: 40,
  });

  const fetchChartData = async () => {
    if (!shifts || shifts.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    if (!lineId || !dateRange.start || !dateRange.end) {
      return;
    }
    if (!token) {
      setError("Authentication required");
      return;
    }

    const cached = chartCache.getCachedData(
      "pc.line_efficiency",
      lineId,
      dateRange,
      shifts,
    );
    if (cached) {
      setRows(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await ChartDataService.fetchLineEfficiency(
        {
          line_bk: lineId,
          date_from: dateRange.start,
          date_to: dateRange.end,
          shift_name: shifts,
        },
        token,
      );

      if (response.success && response.data) {
        // Backend may return the array directly or wrapped in { results }.
        const raw = response.data as unknown;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray((raw as { results?: unknown }).results)
            ? (raw as { results: unknown[] }).results
            : [];

        const parsed: LineEfficiencyRow[] = (
          arr as Record<string, unknown>[]
        ).map((r) => ({
          shift_date: String(r.shift_date ?? ""),
          shift_name: String(r.shift_name ?? ""),
          machine_sn: String(r.machine_sn ?? ""),
          machine_name: String(r.machine_name ?? r.machine_sn ?? ""),
          efficiency: Number(r.efficiency) || 0,
          utilization: Number(r.utilization) || 0,
        }));

        setRows(parsed);
        chartCache.setCachedData(
          "pc.line_efficiency",
          lineId,
          dateRange,
          shifts,
          parsed,
        );
      } else if (response.error) {
        setError(response.error);
      }
    } catch (err) {
      console.error("❌ LineEfficiencyChart: Failed to fetch data:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId, dateRange.start, dateRange.end, shifts, token]);

  // Build grouped-column data: categories = shift buckets, one series per machine.
  const { categories, series } = useMemo(() => {
    const dataKey = activeTab === "utilization" ? "utilization" : "efficiency";

    // Category = "shift_date shift_name" (chronological, then by shift name).
    const catSet = new Map<string, { date: string; shift: string }>();
    for (const r of rows) {
      const cat = `${r.shift_date} ${r.shift_name}`;
      if (!catSet.has(cat))
        catSet.set(cat, { date: r.shift_date, shift: r.shift_name });
    }
    const cats = Array.from(catSet.keys()).sort((a, b) => {
      const A = catSet.get(a)!;
      const B = catSet.get(b)!;
      return A.date === B.date
        ? A.shift.localeCompare(B.shift)
        : A.date.localeCompare(B.date);
    });

    // One series per machine, keyed by serial; label uses machine_name.
    const machines = new Map<string, string>();
    for (const r of rows) {
      if (!machines.has(r.machine_sn))
        machines.set(r.machine_sn, r.machine_name);
    }

    // Fast lookup: `${cat}|${machine_sn}` -> value (already a 0–1 ratio → %).
    const valueByCatMachine = new Map<string, number>();
    for (const r of rows) {
      valueByCatMachine.set(
        `${r.shift_date} ${r.shift_name}|${r.machine_sn}`,
        (r[dataKey] as number) * 100,
      );
    }

    const builtSeries = Array.from(machines.entries()).map(
      ([sn, name], index) => ({
        name: name || sn,
        data: cats.map((cat) => {
          const v = valueByCatMachine.get(`${cat}|${sn}`);
          return v === undefined ? null : Math.round(v * 10) / 10;
        }),
        color: getSeriesColor(index),
      }),
    );

    return { categories: cats, series: builtSeries };
  }, [rows, activeTab, getSeriesColor]);

  return (
    <BaseChart
      commentProps={{
        chartType:
          activeTab === "utilization" ? "line_utilization" : "line_efficiency",
        machineBK: lineId,
      }}
      type="column"
      height={400}
      categories={categories}
      series={series}
      yAxis={{ min: 0, max: 100, format: "{value}%" }}
      loading={loading}
      error={error}
      onRetry={fetchChartData}
      legend={{ enabled: true, position: "right" }}
      tooltip={{ suffix: "%" }}
      navigator={{ enabled: true, height: 70, margin: 25 }}
      noDataMessage={
        !lineId
          ? "No line selected"
          : !dateRange.start || !dateRange.end
            ? "Please select a date range"
            : !shifts || shifts.length === 0
              ? "No shifts selected or available for the selected timeframe"
              : activeTab === "utilization"
                ? "No utilization data found for the selected period"
                : "No efficiency data found for the selected period"
      }
    />
  );
};

export default LineEfficiencyChart;
